import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/integrations/supabase/client';
import { WorkspaceService } from '@/services/workspace-service';
// import { DataConfigurationService } from '@/services/data-configuration-service';
import { getCurrentTenantSlug } from '@/utils/tenant-utils';
import { 
  FileText, 
  Plus, 
  Trash2, 
  Type, 
  Hash, 
  Calendar, 
  ChevronDown, 
  AlignLeft, 
  ToggleLeft,
  Package,
  Upload,
  Database,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings,
  Eye,
  EyeOff,
  X,
  Table,
  Clipboard,
  GitCompare,
  AlertTriangle,
  FileCheck,
  Circle,
  Target
} from 'lucide-react';
import ParseMapModal from './ParseMapModal';
import MarkerPickerModal from './MarkerPickerModal';

type FieldType = 'text' | 'number' | 'date' | 'dropdown' | 'textarea' | 'boolean' | 'radio';
type DataCollectionType = 'form' | 'data' | 'visual-defect';
type VisualRequirement = '3d-visual' | '2d-visual' | 'no-visual';

interface LogField {
  id: string;
  title: string;
  type: FieldType;
  required: boolean;
  options?: string[]; // For dropdown fields
  useInName?: boolean; // New property to control if field should be used in submission name
}

interface FileUpload {
  file: File | null;
  isValid: boolean;
  errorMessage: string;
}

interface DataPoint {
  featureLabel: string;
  x: number;
  y: number;
  z: number;
}

interface NominalInfo {
  featureLabel: string;
  characteristic: string;
  nominal: number;
  upperTolerance: number;
  lowerTolerance: number;
}

interface DataConfig {
  partNumber: string;
  description: string;
  displayName: string;
  visualRequirement: VisualRequirement;
  stlFile?: FileUpload;
  imageFiles?: FileUpload[];  // Changed to array for multiple images
  dataPoints?: DataPoint[];
  dataPointsText?: string;
  nominalInfo?: NominalInfo[];
  nominalInfoText?: string;
}

interface LogConfig {
  dataType: DataCollectionType;
  formTitle: string;
  fields: LogField[];
  dataConfig?: DataConfig;
}

interface ManufacturingProcess {
  id: string;
  name: string;
  description: string | null;
}

interface ComparisonResult {
  isValid: boolean;
  markerDuplicates: string[];
  missingInNominal: string[];
  missingInMarker: string[];
  warnings: string[];
}

interface ParsingConfig {
  id: string;
  name: string;
  fileType: string;
  description: string;
  config: any; // Will define structure later
}

interface DataImportConfig {
  useExistingFormat: boolean;
  selectedConfigId: string;
  uploadedFile: File | null;
  isValidated: boolean;
  validationResult: string;
}

interface LogConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  station: ManufacturingProcess | null;
  onSave: (config: LogConfig) => void;
  productId?: string;
  onConfigSaved?: () => void;
  onRefreshData?: () => Promise<void>; // New prop for direct refresh
}

const LogConfigModal = ({ isOpen, onClose, station, onSave, productId, onConfigSaved, onRefreshData }: LogConfigModalProps) => {
  const [dataType, setDataType] = useState<DataCollectionType>('form');
  const [logFormTitle, setLogFormTitle] = useState('');
  const [logFields, setLogFields] = useState<LogField[]>([]);
  // Data configuration state
  const [partNumber, setPartNumber] = useState('');
  const [description, setDescription] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [visualRequirement, setVisualRequirement] = useState<VisualRequirement>('no-visual');
  // 3D workflow state
  const [stlFile, setStlFile] = useState<FileUpload>({ file: null, isValid: false, errorMessage: '' });
  // 2D workflow state
  const [imageFiles, setImageFiles] = useState<FileUpload[]>([]);
  const [showMarkerPickerModal, setShowMarkerPickerModal] = useState(false);
  const [dataPointsText, setDataPointsText] = useState('');
  const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
  const [dataPointsValid, setDataPointsValid] = useState(false);
  const [dataPointsError, setDataPointsError] = useState('');
  // Nominal information state
  const [nominalInfoText, setNominalInfoText] = useState('');
  const [nominalInfo, setNominalInfo] = useState<NominalInfo[]>([]);
  const [nominalInfoValid, setNominalInfoValid] = useState(false);
  const [nominalInfoError, setNominalInfoError] = useState('');
  // Comparison state
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  // Data import configuration state
  const [dataImportConfig, setDataImportConfig] = useState<DataImportConfig>({
    useExistingFormat: true,
    selectedConfigId: '',
    uploadedFile: null,
    isValidated: false,
    validationResult: ''
  });
  const [parsingConfigs, setParsingConfigs] = useState<ParsingConfig[]>([]);
  const [showParseMapModal, setShowParseMapModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // File validation functions
  const validateStlFile = (file: File): { isValid: boolean; errorMessage: string } => {
    const allowedExtensions = ['.stl'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        errorMessage: 'Only .stl files are allowed for 3D models'
      };
    }

    // Additional size check (optional - adjust as needed)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return {
        isValid: false,
        errorMessage: 'File size must be less than 50MB'
      };
    }

    return { isValid: true, errorMessage: '' };
  };

  const validateImageFile = (file: File): { isValid: boolean; errorMessage: string } => {
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        errorMessage: 'Only image files are allowed (.png, .jpg, .jpeg, .gif, .bmp, .webp)'
      };
    }

    // Size check for images (typically smaller than STL files)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        isValid: false,
        errorMessage: 'Image file size must be less than 10MB'
      };
    }

    return { isValid: true, errorMessage: '' };
  };

  const validateDataPoints = (text: string): { isValid: boolean; errorMessage: string; dataPoints: DataPoint[] } => {
    if (!text.trim()) {
      return {
        isValid: false,
        errorMessage: 'Please enter data points',
        dataPoints: []
      };
    }

    const lines = text.split('\n').filter(line => line.trim());
    const dataPoints: DataPoint[] = [];
    const seenLabels = new Set<string>();
    const is2D = visualRequirement === '2d-visual';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Split by tabs first (Excel paste), then by commas as fallback
      let columns = line.split('\t');
      if (columns.length === 1) {
        columns = line.split(',');
      }
      
      // Clean up columns
      columns = columns.map(col => col.trim());
      
      // For 2D: expect 3 columns (Feature Label, X, Y)
      // For 3D: expect exactly 4 columns (Feature Label, X, Y, Z)
      const expectedColumns = is2D ? 3 : 4;
      
      if (columns.length !== expectedColumns) {
        const expectedFormat = is2D 
          ? 'exactly 3 columns (Feature Label, X, Y) for 2D visual'
          : 'exactly 4 columns (Feature Label, X, Y, Z) for 3D visual';
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Must have ${expectedFormat}, found ${columns.length}`,
          dataPoints: []
        };
      }

      // First column should be a string (any non-empty value is acceptable)
      if (!columns[0] || columns[0].length === 0) {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Feature Label cannot be empty`,
          dataPoints: []
        };
      }

      // Check for duplicate feature labels in marker positions
      if (seenLabels.has(columns[0])) {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Feature Label "${columns[0]}" is already used. Marker positions must have unique feature labels.`,
          dataPoints: []
        };
      }
      seenLabels.add(columns[0]);

      // Parse coordinates based on visual requirement
      const x = Number(columns[1]);
      const y = Number(columns[2]);
      let z = 0; // Default for 2D

      if (isNaN(x) || columns[1] === '') {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: X coordinate must be a number, found "${columns[1]}"`,
          dataPoints: []
        };
      }

      if (isNaN(y) || columns[2] === '') {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Y coordinate must be a number, found "${columns[2]}"`,
          dataPoints: []
        };
      }

      // Handle Z coordinate for 3D
      if (!is2D) {
        z = Number(columns[3]);
        if (isNaN(z) || columns[3] === '') {
          return {
            isValid: false,
            errorMessage: `Line ${i + 1}: Z coordinate must be a number, found "${columns[3]}"`,
            dataPoints: []
          };
        }
      }

      dataPoints.push({
        featureLabel: columns[0],
        x: x,
        y: y,
        z: z
      });
    }

    return {
      isValid: true,
      errorMessage: '',
      dataPoints: dataPoints
    };
  };

  const validateNominalInfo = (text: string): { isValid: boolean; errorMessage: string; nominalInfo: NominalInfo[] } => {
    if (!text.trim()) {
      return {
        isValid: false,
        errorMessage: 'Please enter nominal information',
        nominalInfo: []
      };
    }

    const lines = text.split('\n').filter(line => line.trim());
    const nominalInfo: NominalInfo[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Split by tabs first (Excel paste), then by commas as fallback
      let columns = line.split('\t');
      if (columns.length === 1) {
        columns = line.split(',');
      }
      
      // Clean up columns
      columns = columns.map(col => col.trim());
      
      if (columns.length !== 5) {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Must have exactly 5 columns (Feature Label, Characteristic, Nominal, Upper Tolerance, Lower Tolerance), found ${columns.length}`,
          nominalInfo: []
        };
      }

      // First column should be a string (Feature Label)
      if (!columns[0] || columns[0].length === 0) {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Feature Label cannot be empty`,
          nominalInfo: []
        };
      }

      // Second column should be a string (Characteristic)
      if (!columns[1] || columns[1].length === 0) {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Characteristic cannot be empty`,
          nominalInfo: []
        };
      }

      // Columns 3, 4, 5 should be numbers (Nominal, Upper Tolerance, Lower Tolerance)
      const nominal = Number(columns[2]);
      const upperTolerance = Number(columns[3]);
      const lowerTolerance = Number(columns[4]);

      if (isNaN(nominal) || columns[2] === '') {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Nominal must be a number, found "${columns[2]}"`,
          nominalInfo: []
        };
      }

      if (isNaN(upperTolerance) || columns[3] === '') {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Upper Tolerance must be a number, found "${columns[3]}"`,
          nominalInfo: []
        };
      }

      if (isNaN(lowerTolerance) || columns[4] === '') {
        return {
          isValid: false,
          errorMessage: `Line ${i + 1}: Lower Tolerance must be a number, found "${columns[4]}"`,
          nominalInfo: []
        };
      }

      nominalInfo.push({
        featureLabel: columns[0],
        characteristic: columns[1],
        nominal: nominal,
        upperTolerance: upperTolerance,
        lowerTolerance: lowerTolerance
      });
    }

    return {
      isValid: true,
      errorMessage: '',
      nominalInfo: nominalInfo
    };
  };

  const compareFeatures = (): ComparisonResult => {
    const markerLabels = new Set(dataPoints.map(dp => dp.featureLabel));
    const nominalLabels = new Set(nominalInfo.map(ni => ni.featureLabel));
    
    // Check for duplicates in marker positions (should not happen due to validation, but double-check)
    const markerLabelArray = dataPoints.map(dp => dp.featureLabel);
    const markerDuplicates = markerLabelArray.filter((label, index) => 
      markerLabelArray.indexOf(label) !== index
    );
    
    // Find features in marker positions but not in nominal information
    const missingInNominal = Array.from(markerLabels).filter(label => !nominalLabels.has(label));
    
    // Find features in nominal information but not in marker positions
    const missingInMarker = Array.from(nominalLabels).filter(label => !markerLabels.has(label));
    
    // Generate warnings for duplicate characteristics in nominal info
    const warnings: string[] = [];
    const nominalDuplicates = nominalInfo.reduce((acc, info) => {
      const key = `${info.featureLabel}-${info.characteristic}`;
      if (acc[key]) {
        acc[key]++;
      } else {
        acc[key] = 1;
      }
      return acc;
    }, {} as Record<string, number>);
    
    Object.entries(nominalDuplicates).forEach(([key, count]) => {
      if (count > 1) {
        const [featureLabel, characteristic] = key.split('-');
        warnings.push(`Feature "${featureLabel}" has ${count} entries for characteristic "${characteristic}"`);
      }
    });
    
    const isValid = markerDuplicates.length === 0 && missingInNominal.length === 0 && missingInMarker.length === 0;
    
    return {
      isValid,
      markerDuplicates,
      missingInNominal,
      missingInMarker,
      warnings
    };
  };

  const handleStlFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = validateStlFile(file);
      setStlFile({
        file: file,
        isValid: validation.isValid,
        errorMessage: validation.errorMessage
      });
    } else {
      setStlFile({ file: null, isValid: false, errorMessage: '' });
    }
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = validateImageFile(file);
      setImageFiles(prev => [...prev, {
        file: file,
        isValid: validation.isValid,
        errorMessage: validation.errorMessage
      }]);
    } else {
      setImageFiles([]);
    }
  };

  const handleDataPointsChange = (text: string) => {
    setDataPointsText(text);
    
    if (!text.trim()) {
      setDataPoints([]);
      setDataPointsValid(false);
      setDataPointsError('');
      return;
    }

    const validation = validateDataPoints(text);
    setDataPoints(validation.dataPoints);
    setDataPointsValid(validation.isValid);
    setDataPointsError(validation.errorMessage);
  };

  const handleNominalInfoChange = (text: string) => {
    setNominalInfoText(text);
    const result = validateNominalInfo(text);
    setNominalInfo(result.nominalInfo);
    setNominalInfoValid(result.isValid);
    setNominalInfoError(result.errorMessage);
  };

  const handleMarkersUpdated = (updatedMarkers: DataPoint[]) => {
    setDataPoints(updatedMarkers);
    // For 2D, we consider markers valid if any have been positioned
    const hasPositionedMarkers = updatedMarkers.some(m => m.x !== 0 || m.y !== 0);
    setDataPointsValid(hasPositionedMarkers);
    setDataPointsError('');
  };

  const addLogField = () => {
    if (logFields.length >= 10) {
      toast({
        title: "Maximum fields reached",
        description: "You can only add up to 10 fields per form",
        variant: "destructive",
      });
      return;
    }

    const newField: LogField = {
      id: `field_${Date.now()}`,
      title: '',
      type: 'text',
      required: false,
      useInName: false,
      options: []
    };
    setLogFields([...logFields, newField]);
  };

  const updateLogField = (index: number, updates: Partial<LogField>) => {
    const updatedFields = [...logFields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setLogFields(updatedFields);
  };

  const removeLogField = (index: number) => {
    const updatedFields = logFields.filter((_, i) => i !== index);
    setLogFields(updatedFields);
  };

  const handleCompareFeatures = () => {
    if (!dataPointsValid || !nominalInfoValid) {
      toast({
        title: "Cannot compare",
        description: "Both marker positions and nominal information must be valid before comparing",
        variant: "destructive",
      });
      return;
    }

    const result = compareFeatures();
    setComparisonResult(result);
    setShowComparison(true);

    if (result.isValid) {
      toast({
        title: "Comparison successful",
        description: "All features are properly matched between marker positions and nominal information",
      });
    } else {
      toast({
        title: "Comparison issues found",
        description: "Please review the comparison results and fix any issues",
        variant: "destructive",
      });
    }
  };

  const handleDataImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setDataImportConfig(prev => ({
        ...prev,
        uploadedFile: null,
        isValidated: false,
        validationResult: ''
      }));
      return;
    }

    // Validate file type
    const allowedExtensions = ['.csv', '.json', '.txt'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a .csv, .json, or .txt file",
        variant: "destructive",
      });
      return;
    }

    setDataImportConfig(prev => ({
      ...prev,
      uploadedFile: file,
      isValidated: false,
      validationResult: ''
    }));
  };

  const handleValidateFile = async () => {
    if (!dataImportConfig.uploadedFile) {
      toast({
        title: "No file selected",
        description: "Please select a file to validate",
        variant: "destructive",
      });
      return;
    }

    if (dataImportConfig.useExistingFormat && !dataImportConfig.selectedConfigId) {
      toast({
        title: "No format selected",
        description: "Please select a parsing format",
        variant: "destructive",
      });
      return;
    }

    // Mock validation - will implement actual parsing logic later
    try {
      const fileContent = await dataImportConfig.uploadedFile.text();
      const lines = fileContent.split('\n').slice(0, 5); // Preview first 5 lines
      
      setDataImportConfig(prev => ({
        ...prev,
        isValidated: true,
        validationResult: `File validated successfully!\nPreview:\n${lines.join('\n')}`
      }));

      toast({
        title: "File validation successful",
        description: "File format matches the selected parsing configuration",
      });
    } catch (error) {
      setDataImportConfig(prev => ({
        ...prev,
        isValidated: false,
        validationResult: 'Failed to read file content'
      }));

      toast({
        title: "Validation failed",
        description: "Unable to process the uploaded file",
        variant: "destructive",
      });
    }
  };

  const handleCreateNewFormat = () => {
    if (!dataImportConfig.uploadedFile) {
      toast({
        title: "No file selected",
        description: "Please upload a sample file to create a new parsing format",
        variant: "destructive",
      });
      return;
    }

    setShowParseMapModal(true);
  };

  const handleParseMapSave = (config: any) => {
    // Add the new configuration to the list
    const newConfig: ParsingConfig = {
      id: Date.now().toString(), // Temporary ID - will be replaced with database ID
      name: config.name,
      fileType: config.fileType,
      description: config.description,
      config: config
    };

    setParsingConfigs(prev => [...prev, newConfig]);
    
    // Select the new configuration and close the modal
    setDataImportConfig(prev => ({
      ...prev,
      selectedConfigId: newConfig.id,
      useExistingFormat: false // Set to false so the new config gets saved to database
    }));
    
    setShowParseMapModal(false);
    
    toast({
      title: "Parsing Format Ready",
      description: `"${config.name}" is now selected and ready to use for data import.`,
    });
  };

  const handleSave = async () => {
    
    // Now check for required information
    if (!user) {
      
      toast({
        title: "Authentication Error",
        description: "Please log in to create data configurations",
        variant: "destructive",
      });
      return;
    }

    if (!station) {
      
      toast({
        title: "Station Required",
        description: "Please select a manufacturing station to configure",
        variant: "destructive",
      });
      return;
    }

    const fallbackProductId = (station as any)?.product_id as string | undefined;
    const effectiveProductId = productId ?? fallbackProductId;
    if (!effectiveProductId) {
      toast({
        title: "Project Required",
        description: "Please select a project to configure data collection for",
        variant: "destructive",
      });
      return;
    }

    // Get current workspace
    const currentSlug = getCurrentTenantSlug();
    const workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
    
    if (!workspace) {
      toast({
        title: "Error",
        description: "Could not determine current workspace",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      if (dataType === 'form') {
        // Handle form configuration (existing logic)
        if (!logFormTitle.trim()) {
          toast({
            title: "Form title required",
            description: "Please enter a title for the form",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }

        const config = {
          dataType: dataType as DataCollectionType,
          formTitle: logFormTitle,
          fields: logFields.map(field => ({
            ...field,
            options: field.options ? field.options.filter(opt => opt.trim()) : undefined
          })),
          dataConfig: undefined
        };

        const configData = {
          fields: config.fields as any,
          formTitle: config.formTitle
        };

        const { error } = await (supabase as any)
          .from('watt_manual_config')
          .insert({
            workspace_id: workspace.id,
            product_id: effectiveProductId,
            manufacturing_process_id: station.id,
            data_type: config.dataType,
            form_title: config.formTitle,
            config_data: configData as any,
            created_by: user.id
          });

        if (error) {
          console.error('Error saving form configuration:', error);
          toast({
            title: "Error",
            description: "Failed to save form configuration to database",
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Configuration saved",
          description: `Log form "${config.formTitle}" has been configured for ${station.name}`,
        });

      } else if (dataType === 'data') {
        // Handle data configuration - save to proper database tables
        if (!partNumber.trim() || !description.trim() || !displayName.trim()) {
          toast({
            title: "Required fields missing",
            description: "Please fill in Part Number, Description, and Display Name",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }



        // For 3D visuals: require marker positions, nominal info, and feature comparison
        if (visualRequirement === '3d-visual') {
          if (!dataPoints.length || !nominalInfo.length) {
            toast({
              title: "Data validation required",
              description: "Please add marker positions and nominal information, then run comparison",
              variant: "destructive",
            });
            setSaving(false);
            return;
          }

          if (!comparisonResult?.isValid) {
            toast({
              title: "Data validation failed",
              description: "Please fix the comparison issues before saving",
              variant: "destructive",
            });
            setSaving(false);
            return;
          }
        }

        // For 2D visuals: only require nominal info
        if (visualRequirement === '2d-visual') {
          if (!nominalInfo.length) {
            toast({
              title: "Nominal information required",
              description: "Please add nominal information for 2D visual",
              variant: "destructive",
            });
            setSaving(false);
            return;
          }

          // Check if markers have been positioned
          const hasPositionedMarkers = dataPoints.some(m => m.x !== 0 || m.y !== 0);
          if (!hasPositionedMarkers) {
            toast({
              title: "Markers required",
              description: "Please use 'Pick Markers' to position markers on the image",
              variant: "destructive",
            });
            setSaving(false);
            return;
          }
        }

        // Validate parsing configuration is selected
        if (!dataImportConfig.selectedConfigId) {
          toast({
            title: "Parsing configuration required",
            description: "Please select an existing parsing format or create a new one",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }

        // Validate visual files are provided when required
        if (visualRequirement === '3d-visual' && (!stlFile.file || !stlFile.isValid)) {
          toast({
            title: "STL file required",
            description: "Please upload a valid STL file for 3D visual requirement",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }

        if (visualRequirement === '2d-visual' && (!imageFiles.length || !imageFiles.some(file => file.isValid))) {
          toast({
            title: "Image files required",
            description: "Please upload valid image files for 2D visual requirement",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }

        
        // Step 1a: Upload visual file if provided (STL for 3D or image for 2D)
        let stlFilePath = null;
        let uploadedImages = [];

        if (visualRequirement === '3d-visual' && stlFile.file && stlFile.isValid) {
          
          // Sanitize partNumber for S3 compatibility
          const sanitizedPartNumber = partNumber.replace(/[^a-zA-Z0-9.-]/g, '_');
          const fileName = `${sanitizedPartNumber}_${Date.now()}.stl`;
          const filePath = `${workspace.id}/${sanitizedPartNumber}/${fileName}`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('stl-files')
            .upload(filePath, stlFile.file, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Error uploading STL file:', uploadError);
            toast({
              title: "Error",
              description: `Failed to upload STL file: ${uploadError.message}`,
              variant: "destructive",
            });
            setSaving(false);
            return;
          }

          stlFilePath = uploadData.path;
          console.log('STL file uploaded successfully:', stlFilePath);
        } else if (visualRequirement === '2d-visual' && imageFiles.length && imageFiles.some(file => file.isValid)) {
          
          // Upload all valid image files
          
          for (let i = 0; i < imageFiles.length; i++) {
            const imageFile = imageFiles[i];
            if (!imageFile.isValid) continue;
            
            // Sanitize partNumber for S3 compatibility
            const sanitizedPartNumber = partNumber.replace(/[^a-zA-Z0-9.-]/g, '_');
            const fileExtension = imageFile.file.name.split('.').pop();
            const fileName = `${sanitizedPartNumber}_${Date.now()}_${i}.${fileExtension}`;
            const filePath = `${workspace.id}/${sanitizedPartNumber}/${fileName}`;
            
            const { data: uploadData, error: uploadError } = await supabase.storage
              .from('image-files')
              .upload(filePath, imageFile.file, {
                cacheControl: '3600',
                upsert: false
              });

            if (uploadError) {
              console.error('Error uploading image file:', uploadError);
              toast({
                title: "Error",
                description: `Failed to upload image file: ${uploadError.message}`,
                variant: "destructive",
              });
              setSaving(false);
              return;
            }

            uploadedImages.push({
              path: uploadData.path,
              name: `Page ${i + 1}`,
              order: i + 1
            });
          }

          
        }

        // Step 1b: Save to part_configurations table
        
        const { data: partConfigData, error: partConfigError } = await (supabase as any)
          .from('part_configurations')
          .insert({
            workspace_id: workspace.id,
            product_id: effectiveProductId,
            manufacturing_process_id: station.id,
            part_number: partNumber,
            display_name: displayName,
            description: description,
            marker_positions: dataPoints,
            visual_requirement: visualRequirement,
            stl_file_path: stlFilePath,
            image_files: uploadedImages || []
          })
          .select()
          .single();

        if (partConfigError) {
          console.error('Error saving part configuration:', partConfigError);
          
          // Handle duplicate key constraint specifically
          if (partConfigError.code === '23505') {
            toast({
              title: "Duplicate Part Number",
              description: `A part configuration with part number "${partNumber}" already exists in this workspace. Please use a different part number.`,
              variant: "destructive",
            });
          } else {
            toast({
              title: "Error",
              description: `Failed to save part configuration: ${partConfigError.message}`,
              variant: "destructive",
            });
          }
          setSaving(false);
          return;
        }

        

        // Step 2: Save nominal information to nominal_information table
        const nominalInserts = nominalInfo.map(ni => ({
          part_configuration_id: partConfigData.id,
          feature_label: ni.featureLabel,
          characteristic: ni.characteristic,
          nominal: ni.nominal,
          upper_tolerance: ni.upperTolerance,
          lower_tolerance: ni.lowerTolerance
        }));

        
        const { error: nominalError } = await (supabase as any)
          .from('nominal_information')
          .insert(nominalInserts);

        if (nominalError) {
          console.error('Error saving nominal information:', nominalError);
          toast({
            title: "Error",
            description: `Failed to save nominal information: ${nominalError.message}`,
            variant: "destructive",
          });
          setSaving(false);
          return;
        }

        

        // Step 3: Create and save parsing configuration (if new format was created)
        let parsingConfigData = null;
        
        if (!dataImportConfig.useExistingFormat && dataImportConfig.selectedConfigId) {
          // Find the parsing config that was created through ParseMapModal
          const selectedParsingConfig = parsingConfigs.find(pc => pc.id === dataImportConfig.selectedConfigId);
          
          
          
          if (selectedParsingConfig) {
            
            
            try {
              const { data: savedParsingConfig, error: parsingError } = await (supabase as any)
                .from('parsing_configurations')
                .insert({
                  workspace_id: workspace.id,
                  name: selectedParsingConfig.name,
                  description: selectedParsingConfig.description || '',
                  file_type: selectedParsingConfig.fileType || 'csv',
                  config_data: selectedParsingConfig.config || {}
                })
                .select()
                .single();

              if (parsingError) {
                console.error('❌ Error saving parsing configuration:', parsingError);
                toast({
                  title: "Error",
                  description: `Failed to save parsing configuration: ${parsingError.message}`,
                  variant: "destructive",
                });
                return;
              }

              if (savedParsingConfig) {
                
                parsingConfigData = savedParsingConfig;
              } else {
                console.error('❌ No data returned from parsing configuration save');
                toast({
                  title: "Error",
                  description: "Failed to save parsing configuration - no data returned",
                  variant: "destructive",
                });
                return;
              }
            } catch (err) {
              console.error('❌ Exception saving parsing configuration:', err);
              toast({
                title: "Error",
                description: `Failed to save parsing configuration: ${err instanceof Error ? err.message : 'Unknown error'}`,
                variant: "destructive",
              });
              return;
            }
          } else {
            console.error('❌ Selected parsing config not found in local array');
            toast({
              title: "Error",
              description: "Selected parsing configuration not found",
              variant: "destructive",
            });
            return;
          }
        } else if (dataImportConfig.useExistingFormat && dataImportConfig.selectedConfigId) {
          // Using existing format - retrieve the parsing config from database
          
          
          // For existing formats, we need to get the actual UUID from the database
          // The selectedConfigId might be a temporary ID from the local parsingConfigs array
          const selectedParsingConfig = parsingConfigs.find(pc => pc.id === dataImportConfig.selectedConfigId);
          
          if (selectedParsingConfig) {
            // Check if this is a real UUID or a temporary numeric ID
            const isTemporaryId = /^\d+$/.test(selectedParsingConfig.id);
            
            if (isTemporaryId) {
              // This is a temporary ID, need to find the real one by name
              const { data: realParsingConfig, error: fetchError } = await (supabase as any)
                .from('parsing_configurations')
                .select('id')
                .eq('workspace_id', workspace.id)
                .eq('name', selectedParsingConfig.name)
                .single();
                
              if (fetchError) {
                console.error('Error fetching real parsing configuration:', fetchError);
                toast({
                  title: "Error",
                  description: "Failed to find parsing configuration in database",
                  variant: "destructive",
                });
                setSaving(false);
                return;
              }
              
              parsingConfigData = realParsingConfig;
            } else {
              // This is already a real UUID, use it as-is
              parsingConfigData = { id: selectedParsingConfig.id };
            }
          }
        }

        // Step 4: Also save a reference in watt_manual_config for UI compatibility
        
        const { error: manualConfigError } = await (supabase as any)
          .from('watt_manual_config')
          .insert({
            workspace_id: workspace.id,
            product_id: effectiveProductId,
            manufacturing_process_id: station.id,
            data_type: 'data',
            form_title: displayName,
            config_data: {
              part_configuration_id: partConfigData.id,
              parsing_configuration_id: parsingConfigData?.id || dataImportConfig.selectedConfigId,
              dataConfig: {
                partNumber,
                description,
                displayName,
                visualRequirement
              }
            } as any,
            created_by: user.id
          });

        if (manualConfigError) {
          console.error('Error saving manual config reference:', manualConfigError);
          // Don't fail the whole operation for this
        }

        toast({
          title: "Configuration saved successfully!",
          description: `Data collection for "${displayName}" has been configured with ${nominalInfo.length} nominal records, ${dataPoints.length} marker positions, and ${parsingConfigData ? 'new' : 'existing'} parsing rules`,
        });

        
      }

      // Refresh data immediately after successful save (like project creation)
      if (onRefreshData) {
        await onRefreshData();
      }

    } catch (error) {
      console.error('Error saving configuration:', error);
      toast({
        title: "Error",
        description: "Failed to save configuration",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }

    handleClose();
  };

  const handleClose = () => {
    setDataType('form');
    setLogFormTitle('');
    setLogFields([]);
    setPartNumber('');
    setDescription('');
    setDisplayName('');
    setVisualRequirement('3d-visual');
    setStlFile({ file: null, isValid: false, errorMessage: '' });
    setImageFiles([]);
    setDataPointsText('');
    setDataPoints([]);
    setDataPointsValid(false);
    setDataPointsError('');
    setNominalInfoText('');
    setNominalInfo([]);
    setNominalInfoValid(false);
    setNominalInfoError('');
    setComparisonResult(null);
    setShowComparison(false);
    setDataImportConfig({
      useExistingFormat: true,
      selectedConfigId: '',
      uploadedFile: null,
      isValidated: false,
      validationResult: ''
    });
    setShowParseMapModal(false);
    onClose();
  };

  useEffect(() => {
    // Fetch parsing configurations from the active workspace only
    const fetchParsingConfigs = async () => {
      try {
        // Try to infer current workspace from station or user memberships
        let currentWorkspaceId: string | null = null;
        if (station?.id) {
          // Station should include workspace_id in most flows
          // @ts-ignore
          currentWorkspaceId = (station as any).workspace_id || null;
        }
        if (!currentWorkspaceId && user) {
          const { data: memberships } = await (supabase as any)
            .from('workspace_memberships')
            .select('workspace_id, is_default')
            .eq('user_id', user.id)
            .eq('status', 'active');
          if (memberships && memberships.length > 0) {
            const preferred = memberships.find((m: any) => m.is_default) || memberships[0];
            currentWorkspaceId = preferred.workspace_id;
          }
        }

        const query = (supabase as any)
          .from('parsing_configurations')
          .select('*');
        const { data: configs, error } = currentWorkspaceId
          ? await query.eq('workspace_id', currentWorkspaceId)
          : await query;

        if (error) {
          console.error('Error fetching parsing configurations:', error);
          toast({
            title: "Error",
            description: "Failed to load parsing configurations",
            variant: "destructive",
          });
          return;
        }

        // Transform database records to match ParsingConfig interface
        const transformedConfigs = configs?.map((config: any) => ({
          id: config.id,
          name: config.name,
          fileType: config.file_type,
          description: config.description || '',
          config: config.config_data || {}
        })) || [];

        setParsingConfigs(transformedConfigs);
      } catch (error) {
        console.error('Error fetching parsing configurations:', error);
        toast({
          title: "Error",
          description: "Failed to load parsing configurations",
          variant: "destructive",
        });
      }
    };

    fetchParsingConfigs();
  }, [toast, station, user]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Data Collection</DialogTitle>
          <DialogDescription>
            Set up manual log forms for {station?.name}. Configure up to 10 fields with different types.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Data Collection Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="data-type">Data Collection Type</Label>
            <Select
              value={dataType}
              onValueChange={(value: DataCollectionType) => setDataType(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select data collection type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="form">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Form
                  </div>
                </SelectItem>
                <SelectItem value="data">
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    Data
                  </div>
                </SelectItem>
                <SelectItem value="visual-defect">
                  <div className="flex items-center gap-2">
                    <AlignLeft className="w-4 h-4" />
                    Visual Defect
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Form Configuration - Only show when 'form' is selected */}
          {dataType === 'form' && (
            <>
              {/* Form Title */}
              <div className="space-y-2">
                <Label htmlFor="form-title">Form Title</Label>
                <Input
                  id="form-title"
                  placeholder="e.g., Maintenance Log, Shift Handover, Quality Check"
                  value={logFormTitle}
                  onChange={(e) => setLogFormTitle(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Placeholder for Data and Visual Defect types */}
          {dataType === 'data' && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold">Data Collection Configuration</h3>
                </div>
                
                {/* Step 1: Part Number, Description, and Display Name */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="part-number">Part Number *</Label>
                    <Input
                      id="part-number"
                      placeholder="e.g., PN-12345, PART-ABC-001"
                      value={partNumber}
                      onChange={(e) => setPartNumber(e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Input
                      id="description"
                      placeholder="e.g., Main housing component"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="display-name">Display Name *</Label>
                    <Input
                      id="display-name"
                      placeholder="e.g., Housing Assembly"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      This name will appear in dropdowns
                    </p>
                  </div>
                </div>

                {/* Step 2: Visual Requirements */}
                <div className="space-y-2">
                  <Label htmlFor="visual-requirement">Visual Requirements</Label>
                  <Select
                    value={visualRequirement}
                    onValueChange={(value: VisualRequirement) => setVisualRequirement(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select visual requirement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3d-visual">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          3D Visual
                        </div>
                      </SelectItem>
                      <SelectItem value="2d-visual">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          2D Visual
                        </div>
                      </SelectItem>
                      <SelectItem value="no-visual">
                        <div className="flex items-center gap-2">
                          <X className="w-4 h-4" />
                          No Visual
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* 3D Workflow - Only show when 3D Visual is selected */}
                {visualRequirement === '3d-visual' && (
                  <Card className="p-4 border-2 border-primary/20">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Eye className="w-5 h-5 text-primary" />
                        <h4 className="text-md font-semibold">3D Model Upload</h4>
                      </div>

                      {/* STL File Upload */}
                      <div className="space-y-2">
                        <Label htmlFor="stl-file">3D Model File (.stl) *</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="stl-file"
                            type="file"
                            accept=".stl"
                            onChange={handleStlFileChange}
                            className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/80"
                          />
                          {stlFile.file && (
                            <div className="flex items-center gap-1">
                              {stlFile.isValid ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                          )}
                        </div>
                        {stlFile.errorMessage && (
                          <p className="text-sm text-red-500">{stlFile.errorMessage}</p>
                        )}
                        {stlFile.file && stlFile.isValid && (
                          <p className="text-sm text-green-600">✓ {stlFile.file.name} ({(stlFile.file.size / 1024 / 1024).toFixed(2)} MB)</p>
                        )}
                      </div>

                      {/* Marker Positions Table */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Table className="w-4 h-4" />
                          <Label htmlFor="data-points">Marker Positions *</Label>
                        </div>
                        <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <Clipboard className="w-3 h-3" />
                          Paste data from Excel/spreadsheet with columns: Feature Label, X, Y, Z
                        </div>
                        
                        {/* Table Header */}
                        <div className="border rounded-md overflow-hidden">
                          <div className="bg-muted/50 grid grid-cols-4 gap-0 border-b">
                            <div className="p-2 text-xs font-medium border-r">Feature Label</div>
                            <div className="p-2 text-xs font-medium border-r">X</div>
                            <div className="p-2 text-xs font-medium border-r">Y</div>
                            <div className="p-2 text-xs font-medium">Z</div>
                          </div>
                          
                          {/* Data Input Area */}
                          <Textarea
                            id="data-points"
                            placeholder="Paste your data here... Example:&#10;Point1&#9;10.5&#9;20.3&#9;15.7&#10;Point2&#9;5.2&#9;18.9&#9;12.4&#10;Point3&#9;8.7&#9;25.1&#9;19.8"
                            value={dataPointsText}
                            onChange={(e) => handleDataPointsChange(e.target.value)}
                            className="min-h-[120px] resize-none border-0 rounded-none focus:ring-0"
                          />
                        </div>

                        {/* Validation Feedback */}
                        <div className="flex items-center gap-2">
                          {dataPointsText && (
                            <>
                              {dataPointsValid ? (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-sm">
                                    {dataPoints.length} marker position{dataPoints.length !== 1 ? 's' : ''} validated
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-red-500">
                                  <AlertCircle className="w-4 h-4" />
                                  <span className="text-sm">Validation error</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {dataPointsError && (
                          <p className="text-sm text-red-500">{dataPointsError}</p>
                        )}

                        {/* Preview Table - Show parsed data */}
                        {dataPoints.length > 0 && dataPointsValid && (
                          <div className="mt-4">
                            <Label className="text-xs text-muted-foreground">Preview ({dataPoints.length} points):</Label>
                            <div className="border rounded-md mt-1 max-h-32 overflow-y-auto">
                              <div className="bg-muted/30 grid grid-cols-4 gap-0 border-b text-xs">
                                <div className="p-1 font-medium border-r">Feature Label</div>
                                <div className="p-1 font-medium border-r">X</div>
                                <div className="p-1 font-medium border-r">Y</div>
                                <div className="p-1 font-medium">Z</div>
                              </div>
                              {dataPoints.slice(0, 5).map((point, index) => (
                                <div key={index} className="grid grid-cols-4 gap-0 border-b last:border-b-0 text-xs">
                                  <div className="p-1 border-r truncate">{point.featureLabel}</div>
                                  <div className="p-1 border-r">{point.x}</div>
                                  <div className="p-1 border-r">{point.y}</div>
                                  <div className="p-1">{point.z}</div>
                                </div>
                              ))}
                              {dataPoints.length > 5 && (
                                <div className="p-1 text-xs text-muted-foreground text-center">
                                  ... and {dataPoints.length - 5} more points
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Nominal Information Table */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Table className="w-4 h-4" />
                          <Label htmlFor="nominal-info">Nominal Information *</Label>
                        </div>
                        <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <Clipboard className="w-3 h-3" />
                          Paste data from Excel/spreadsheet with columns: Feature Label, Characteristic, Nominal, Upper Tolerance, Lower Tolerance
                        </div>
                        
                        {/* Table Header */}
                        <div className="border rounded-md overflow-hidden">
                          <div className="bg-muted/50 grid grid-cols-5 gap-0 border-b">
                            <div className="p-2 text-xs font-medium border-r">Feature Label</div>
                            <div className="p-2 text-xs font-medium border-r">Characteristic</div>
                            <div className="p-2 text-xs font-medium border-r">Nominal</div>
                            <div className="p-2 text-xs font-medium border-r">Upper Tolerance</div>
                            <div className="p-2 text-xs font-medium">Lower Tolerance</div>
                          </div>
                          
                          {/* Data Input Area */}
                          <Textarea
                            id="nominal-info"
                            placeholder="Paste your data here... Example:&#10;Point1&#9;Diameter&#9;10.5&#9;0.2&#9;-0.1&#10;Point2&#9;Length&#9;25.0&#9;0.3&#9;-0.2&#10;Point3&#9;Height&#9;15.7&#9;0.1&#9;-0.1"
                            value={nominalInfoText}
                            onChange={(e) => handleNominalInfoChange(e.target.value)}
                            className="min-h-[120px] resize-none border-0 rounded-none focus:ring-0"
                          />
                        </div>

                        {/* Validation Feedback */}
                        <div className="flex items-center gap-2">
                          {nominalInfoText && (
                            <>
                              {nominalInfoValid ? (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-sm">
                                    {nominalInfo.length} nominal record{nominalInfo.length !== 1 ? 's' : ''} validated
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-red-500">
                                  <AlertCircle className="w-4 h-4" />
                                  <span className="text-sm">Validation error</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {nominalInfoError && (
                          <p className="text-sm text-red-500">{nominalInfoError}</p>
                        )}

                        {/* Preview Table - Show parsed data */}
                        {nominalInfo.length > 0 && nominalInfoValid && (
                          <div className="mt-4">
                            <Label className="text-xs text-muted-foreground">Preview ({nominalInfo.length} records):</Label>
                            <div className="border rounded-md mt-1 max-h-32 overflow-y-auto">
                              <div className="bg-muted/30 grid grid-cols-5 gap-0 border-b text-xs">
                                <div className="p-1 font-medium border-r">Feature Label</div>
                                <div className="p-1 font-medium border-r">Characteristic</div>
                                <div className="p-1 font-medium border-r">Nominal</div>
                                <div className="p-1 font-medium border-r">Upper Tol</div>
                                <div className="p-1 font-medium">Lower Tol</div>
                              </div>
                              {nominalInfo.slice(0, 5).map((info, index) => (
                                <div key={index} className="grid grid-cols-5 gap-0 border-b last:border-b-0 text-xs">
                                  <div className="p-1 border-r truncate">{info.featureLabel}</div>
                                  <div className="p-1 border-r truncate">{info.characteristic}</div>
                                  <div className="p-1 border-r">{info.nominal}</div>
                                  <div className="p-1 border-r">{info.upperTolerance}</div>
                                  <div className="p-1">{info.lowerTolerance}</div>
                                </div>
                              ))}
                              {nominalInfo.length > 5 && (
                                <div className="p-1 text-xs text-muted-foreground text-center">
                                  ... and {nominalInfo.length - 5} more records
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )}

                {/* 2D Workflow - Only show when 2D Visual is selected */}
                {visualRequirement === '2d-visual' && (
                  <Card className="p-4 border-2 border-primary/20">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="w-5 h-5 text-primary" />
                        <h4 className="text-md font-semibold">2D Image Upload</h4>
                      </div>

                      {/* Image File Upload */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="image-file">Image Files (PNG, JPG, etc.) *</Label>
                          <Button 
                            type="button"
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              const input = document.getElementById('image-file') as HTMLInputElement;
                              if (input) input.click();
                            }}
                            className="text-xs"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Page
                          </Button>
                        </div>
                        
                        {/* Hidden file input */}
                        <Input
                          id="image-file"
                          type="file"
                          accept=".png,.jpg,.jpeg,.gif,.bmp,.webp"
                          onChange={handleImageFileChange}
                          className="hidden"
                        />
                        
                        {/* Image files list */}
                        {imageFiles.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Uploaded Pages ({imageFiles.length}):</Label>
                            <div className="space-y-1">
                              {imageFiles.map((file, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded border">
                                  <div className="flex items-center gap-2">
                                    {file.isValid ? (
                                      <CheckCircle className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <AlertCircle className="w-4 h-4 text-red-500" />
                                    )}
                                    <span className="text-sm">
                                      Page {index + 1}: {file.file?.name}
                                    </span>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setImageFiles(prev => prev.filter((_, i) => i !== index));
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {imageFiles.length === 0 && (
                          <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded">
                            <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No images uploaded yet</p>
                            <p className="text-xs">Click "Add Page" to upload image files</p>
                          </div>
                        )}
                      </div>

                      {/* Nominal Information Table - Same as 3D */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Table className="w-4 h-4" />
                          <Label htmlFor="nominal-info-2d">Nominal Information *</Label>
                        </div>
                        <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <Clipboard className="w-3 h-3" />
                          Paste data from Excel/spreadsheet with columns: Feature Label, Characteristic, Nominal, Upper Tolerance, Lower Tolerance
                        </div>
                        
                        {/* Table Header */}
                        <div className="border rounded-md overflow-hidden">
                          <div className="bg-muted/50 grid grid-cols-5 gap-0 border-b">
                            <div className="p-2 text-xs font-medium border-r">Feature Label</div>
                            <div className="p-2 text-xs font-medium border-r">Characteristic</div>
                            <div className="p-2 text-xs font-medium border-r">Nominal</div>
                            <div className="p-2 text-xs font-medium border-r">Upper Tolerance</div>
                            <div className="p-2 text-xs font-medium">Lower Tolerance</div>
                          </div>
                          
                          {/* Data Input Area */}
                          <Textarea
                            id="nominal-info-2d"
                            placeholder="Paste your data here... Example:&#10;Point1&#9;Diameter&#9;10.5&#9;0.2&#9;-0.1&#10;Point2&#9;Length&#9;25.0&#9;0.3&#9;-0.2&#10;Point3&#9;Height&#9;15.7&#9;0.1&#9;-0.1"
                            value={nominalInfoText}
                            onChange={(e) => handleNominalInfoChange(e.target.value)}
                            className="min-h-[120px] resize-none border-0 rounded-none focus:ring-0"
                          />
                        </div>

                        {/* Validation Feedback */}
                        <div className="flex items-center gap-2">
                          {nominalInfoText && (
                            <>
                              {nominalInfoValid ? (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-sm">
                                    {nominalInfo.length} nominal record{nominalInfo.length !== 1 ? 's' : ''} validated
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-red-500">
                                  <AlertCircle className="w-4 h-4" />
                                  <span className="text-sm">Validation error</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {nominalInfoError && (
                          <p className="text-sm text-red-500">{nominalInfoError}</p>
                        )}

                        {/* Preview Table - Show parsed data */}
                        {nominalInfo.length > 0 && nominalInfoValid && (
                          <div className="mt-4">
                            <Label className="text-xs text-muted-foreground">Preview ({nominalInfo.length} records):</Label>
                            <div className="border rounded-md mt-1 max-h-32 overflow-y-auto">
                              <div className="bg-muted/30 grid grid-cols-5 gap-0 border-b text-xs">
                                <div className="p-1 font-medium border-r">Feature Label</div>
                                <div className="p-1 font-medium border-r">Characteristic</div>
                                <div className="p-1 font-medium border-r">Nominal</div>
                                <div className="p-1 font-medium border-r">Upper Tol</div>
                                <div className="p-1 font-medium">Lower Tol</div>
                              </div>
                              {nominalInfo.slice(0, 5).map((info, index) => (
                                <div key={index} className="grid grid-cols-5 gap-0 border-b last:border-b-0 text-xs">
                                  <div className="p-1 border-r truncate">{info.featureLabel}</div>
                                  <div className="p-1 border-r truncate">{info.characteristic}</div>
                                  <div className="p-1 border-r">{info.nominal}</div>
                                  <div className="p-1 border-r">{info.upperTolerance}</div>
                                  <div className="p-1">{info.lowerTolerance}</div>
                                </div>
                              ))}
                              {nominalInfo.length > 5 && (
                                <div className="p-1 text-xs text-muted-foreground text-center">
                                  ... and {nominalInfo.length - 5} more records
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Pick Markers Button - Show when both image and nominal info are valid */}
                      {imageFiles.length > 0 && imageFiles.some(file => file.isValid) && nominalInfo.length > 0 && nominalInfoValid && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Marker Positioning</Label>
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm"
                              onClick={() => setShowMarkerPickerModal(true)}
                              className="text-xs"
                            >
                              <Target className="w-3 h-3 mr-1" />
                              Pick Markers ({dataPoints.length}/{nominalInfo.length})
                            </Button>
                          </div>
                          
                          {dataPoints.length > 0 && (
                            <div className="text-xs text-green-600">
                              ✓ {dataPoints.length} of {nominalInfo.length} markers positioned
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}

          {dataType === 'visual-defect' && (
            <div className="text-center py-12 text-muted-foreground">
              <AlignLeft className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold mb-2">Visual Defect Configuration</h3>
              <p>Configuration options for visual defect tracking will be available here.</p>
            </div>
          )}

          {/* Fields Configuration - Only show when 'form' is selected */}
          {dataType === 'form' && (
            <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Form Fields ({logFields.length}/10)</Label>
              <Button 
                onClick={addLogField} 
                size="sm" 
                disabled={logFields.length >= 10}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Field
              </Button>
            </div>

            {logFields.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No fields configured yet</p>
                <p className="text-sm">Click "Add Field" to get started</p>
              </div>
            )}

            {logFields.map((field, index) => (
              <Card key={field.id} className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline">Field {index + 1}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLogField(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Field Title</Label>
                      <Input
                        placeholder="e.g., Temperature, Notes, Inspector Name"
                        value={field.title}
                        onChange={(e) => updateLogField(index, { title: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Field Type</Label>
                      <Select
                        value={field.type}
                        onValueChange={(value: FieldType) => updateLogField(index, { type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">
                            <div className="flex items-center gap-2">
                              <Type className="w-4 h-4" />
                              Text
                            </div>
                          </SelectItem>
                          <SelectItem value="number">
                            <div className="flex items-center gap-2">
                              <Hash className="w-4 h-4" />
                              Number
                            </div>
                          </SelectItem>
                          <SelectItem value="date">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              Date
                            </div>
                          </SelectItem>
                          <SelectItem value="dropdown">
                            <div className="flex items-center gap-2">
                              <ChevronDown className="w-4 h-4" />
                              Dropdown
                            </div>
                          </SelectItem>
                          <SelectItem value="textarea">
                            <div className="flex items-center gap-2">
                              <AlignLeft className="w-4 h-4" />
                              Long Text
                            </div>
                          </SelectItem>
                          <SelectItem value="boolean">
                            <div className="flex items-center gap-2">
                              <ToggleLeft className="w-4 h-4" />
                              Yes/No
                            </div>
                          </SelectItem>
                          <SelectItem value="radio">
                            <div className="flex items-center gap-2">
                              <Circle className="w-4 h-4" />
                              Radio Button
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`required-${field.id}`}
                      checked={field.required}
                      onCheckedChange={(checked) => updateLogField(index, { required: checked })}
                    />
                    <Label htmlFor={`required-${field.id}`}>Required field</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`useInName-${field.id}`}
                      checked={field.useInName || false}
                      onCheckedChange={(checked) => updateLogField(index, { useInName: checked })}
                    />
                    <Label htmlFor={`useInName-${field.id}`}>Use in submission name</Label>
                  </div>

                  {field.type === 'dropdown' && (
                    <div className="space-y-2">
                      <Label>Dropdown Options (one per line)</Label>
                      <Textarea
                        rows={3}
                        placeholder="Option 1&#10;Option 2&#10;Option 3"
                        value={field.options?.join('\n') || ''}
                        onChange={(e) => updateLogField(index, { 
                          options: e.target.value.split('\n')
                        })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.stopPropagation();
                          }
                        }}
                        className="resize-none"
                      />
                    </div>
                  )}

                  {field.type === 'radio' && (
                    <div className="space-y-2">
                      <Label>Radio Button Options (one per line)</Label>
                      <Textarea
                        rows={3}
                        placeholder="Option 1&#10;Option 2&#10;Option 3"
                        value={field.options?.join('\n') || ''}
                        onChange={(e) => updateLogField(index, { 
                          options: e.target.value.split('\n')
                        })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.stopPropagation();
                          }
                        }}
                        className="resize-none"
                      />
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
          )}

          {/* Compare Features Button and Results */}
          {dataPointsValid && nominalInfoValid && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-center">
                <Button
                  onClick={handleCompareFeatures}
                  variant="outline"
                  className="gap-2"
                >
                  <GitCompare className="w-4 h-4" />
                  Compare Features
                </Button>
              </div>

              {/* Comparison Results */}
              {showComparison && comparisonResult && (
                <Card className={`p-4 ${comparisonResult.isValid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {comparisonResult.isValid ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      )}
                      <h4 className="font-semibold">
                        {comparisonResult.isValid ? 'All Features Matched' : 'Feature Mismatch Found'}
                      </h4>
                    </div>

                    {/* Errors */}
                    {comparisonResult.markerDuplicates.length > 0 && (
                      <div className="text-red-600">
                        <p className="font-medium">Duplicate Feature Labels in Marker Positions:</p>
                        <ul className="list-disc list-inside text-sm ml-2">
                          {comparisonResult.markerDuplicates.map((label, index) => (
                            <li key={index}>{label}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {comparisonResult.missingInNominal.length > 0 && (
                      <div className="text-red-600">
                        <p className="font-medium">Features in Marker Positions but missing in Nominal Information:</p>
                        <ul className="list-disc list-inside text-sm ml-2">
                          {comparisonResult.missingInNominal.map((label, index) => (
                            <li key={index}>{label}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {comparisonResult.missingInMarker.length > 0 && (
                      <div className="text-red-600">
                        <p className="font-medium">Features in Nominal Information but missing in Marker Positions:</p>
                        <ul className="list-disc list-inside text-sm ml-2">
                          {comparisonResult.missingInMarker.map((label, index) => (
                            <li key={index}>{label}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Warnings */}
                    {comparisonResult.warnings.length > 0 && (
                      <div className="text-amber-600">
                        <p className="font-medium">Warnings:</p>
                        <ul className="list-disc list-inside text-sm ml-2">
                          {comparisonResult.warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Success message */}
                    {comparisonResult.isValid && (
                      <div className="text-green-600">
                        <p className="text-sm">
                          ✓ All {dataPoints.length} marker position{dataPoints.length !== 1 ? 's' : ''} have corresponding nominal information
                        </p>
                        <p className="text-sm">
                          ✓ All {new Set(nominalInfo.map(ni => ni.featureLabel)).size} unique feature{new Set(nominalInfo.map(ni => ni.featureLabel)).size !== 1 ? 's' : ''} in nominal information have marker positions
                        </p>
                        {comparisonResult.warnings.length > 0 && (
                          <p className="text-sm">
                            ⚠️ {comparisonResult.warnings.length} warning{comparisonResult.warnings.length !== 1 ? 's' : ''} (see above)
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Configure Data Import - Only show when comparison is valid */}
          {comparisonResult && comparisonResult.isValid && (
            <Card className="p-4 border-2 border-blue-200 bg-blue-50">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="w-5 h-5 text-blue-600" />
                  <h4 className="text-md font-semibold text-blue-800">Configure Data Import</h4>
                </div>

                {/* Use Existing Format Toggle */}
                <div className="flex items-center space-x-3">
                  <Switch
                    id="use-existing-format"
                    checked={dataImportConfig.useExistingFormat}
                    onCheckedChange={(checked) => 
                      setDataImportConfig(prev => ({ 
                        ...prev, 
                        useExistingFormat: checked,
                        selectedConfigId: '',
                        uploadedFile: null,
                        isValidated: false,
                        validationResult: ''
                      }))
                    }
                  />
                  <Label htmlFor="use-existing-format" className="font-medium">
                    Use existing parsing format
                  </Label>
                </div>

                {/* Existing Format Flow */}
                {dataImportConfig.useExistingFormat ? (
                  <div className="space-y-4">
                    {/* Parsing Format Dropdown */}
                    <div className="space-y-2">
                      <Label>Select Parsing Format</Label>
                      <Select
                        value={dataImportConfig.selectedConfigId}
                        onValueChange={(value) => 
                          setDataImportConfig(prev => ({ 
                            ...prev, 
                            selectedConfigId: value,
                            isValidated: false,
                            validationResult: ''
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a saved parsing configuration" />
                        </SelectTrigger>
                        <SelectContent>
                          {parsingConfigs.map((config) => (
                            <SelectItem key={config.id} value={config.id}>
                              <div className="flex items-center gap-2">
                                <Database className="w-4 h-4" />
                                <div>
                                  <div className="font-medium">{config.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {config.fileType.toUpperCase()} • {config.description}
                                  </div>
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  /* New Format Flow */
                  <div className="space-y-4">
                    {/* File Upload for New Format */}
                    <div className="space-y-2">
                      <Label>Upload Sample File</Label>
                      <div className="text-xs text-muted-foreground mb-2">
                        Upload a sample file to create a new parsing configuration
                      </div>
                      <Input
                        type="file"
                        accept=".csv,.json,.txt"
                        onChange={handleDataImportFileChange}
                        className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700"
                      />
                      {dataImportConfig.uploadedFile && (
                        <p className="text-sm text-muted-foreground">
                          Selected: {dataImportConfig.uploadedFile.name} ({(dataImportConfig.uploadedFile.size / 1024).toFixed(1)} KB)
                        </p>
                      )}
                    </div>

                    {/* Parse and Map Button */}
                    {dataImportConfig.uploadedFile && (
                      <Button
                        onClick={handleCreateNewFormat}
                        variant="outline"
                        className="gap-2"
                      >
                        <Settings className="w-4 h-4" />
                        Parse and Map Fields
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Parse and Map Modal */}
      {dataImportConfig.uploadedFile && (
        <ParseMapModal
          isOpen={showParseMapModal}
          onClose={() => setShowParseMapModal(false)}
          file={dataImportConfig.uploadedFile}
          onSave={handleParseMapSave}
        />
      )}

      {/* Marker Picker Modal for 2D Visual */}
      {showMarkerPickerModal && imageFiles.length > 0 && imageFiles.some(file => file.isValid) && (
        <MarkerPickerModal
          isOpen={showMarkerPickerModal}
          onClose={() => setShowMarkerPickerModal(false)}
          imageFiles={imageFiles}
          nominalInfo={nominalInfo}
          existingMarkers={dataPoints}
          onMarkersUpdated={handleMarkersUpdated}
        />
      )}
    </Dialog>
  );
};

export { LogConfigModal }; 